import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Layers, Upload, ShoppingCart, Wallet, ArrowDownToLine, ListOrdered, Settings, Store } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const NAV = [
  ["", LayoutDashboard, "Overview"], ["templates", Layers, "My Templates"], ["upload", Upload, "Upload"], ["sales", ShoppingCart, "Sales"],
  ["wallet", Wallet, "Wallet"], ["withdraw", ArrowDownToLine, "Withdraw"], ["transactions", ListOrdered, "Transactions"], ["settings", Settings, "Settings"],
];

function BecomeSeller() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ store_name: user.name, bio: "", country: "Nigeria", phone: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/seller/onboard", f); setUser(data); toast.success("Welcome aboard! Your seller account is ready."); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="mx-auto grid max-w-5xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-2">
      <div>
        <p className="eyebrow mb-3">Become a seller</p>
        <h1 className="text-3xl font-bold sm:text-4xl">Sell your templates to thousands of buyers.</h1>
        <ol className="mt-8 space-y-4 text-sm text-muted-foreground">
          {["Design in CorelDRAW, Photoshop or Illustrator", "Export a JPG/PNG preview", "Upload the preview + your editable file", "Get paid for every download — withdraw to your bank"].map((s, i) => (
            <li key={s} className="flex gap-3"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary text-xs font-bold text-white">{i + 1}</span>{s}</li>
          ))}
        </ol>
      </div>
      <form onSubmit={submit} className="card-surface space-y-4 p-6" data-testid="become-seller-form">
        <Store className="text-primary" />
        <div className="space-y-2"><Label>Store / designer name</Label><Input data-testid="store-name-input" value={f.store_name} onChange={(e) => setF({ ...f, store_name: e.target.value })} required minLength={2} /></div>
        <div className="space-y-2"><Label>Short bio</Label><Textarea data-testid="store-bio-input" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} placeholder="What do you design?" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Country</Label><Input data-testid="store-country-input" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></div>
          <div className="space-y-2"><Label>Phone</Label><Input data-testid="store-phone-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
        </div>
        <Button type="submit" className="w-full" data-testid="become-seller-submit-btn">Create seller account</Button>
      </form>
    </div>
  );
}

export default function SellerLayout() {
  const { user } = useAuth();
  if (!user.is_seller) return <BecomeSeller />;
  const cls = ({ isActive }) => `flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`;
  return (
    <div className="mx-auto grid max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[220px_1fr]">
      <aside className="no-scrollbar -mx-4 flex gap-1 overflow-x-auto px-4 lg:sticky lg:top-24 lg:mx-0 lg:flex-col lg:self-start lg:px-0">
        <p className="eyebrow mb-3 hidden px-3 lg:block">Seller studio</p>
        {NAV.map(([to, I, l]) => <NavLink key={l} to={`/seller${to ? `/${to}` : ""}`} end={!to} className={cls} data-testid={`seller-nav-${l.split(" ").pop().toLowerCase()}`}><I size={16} />{l}</NavLink>)}
      </aside>
      <div className="min-w-0"><Outlet /></div>
    </div>
  );
}
